import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import ReadingsService from "../../services/readings.service";
import {
  Button,
  Modal,
  Table,
  Form,
  Input,
  Select,
  Checkbox,
  Alert,
  TimePicker
} from "antd";
import dayjs from "dayjs";
import {
  FixedDataType,
  AllColumnsType,
  ReadingsCheckpoint,
  CustomColumnClicked,
  SavePayload,
} from "../../types/readings.types";
import styles from "../../index.style";
import { CopyOutlined, EditOutlined, PlusCircleFilled, PlusOutlined } from "@ant-design/icons";
import { DateTime } from "luxon";
// import "./Readings.css";
import { useRecoilState } from "recoil";
import { sharedInternalInfoState } from "../../recoil/atoms/sharedInternalAtom";
import { userInfoState } from "../../recoil/atoms/userInfoAtom";
import { useTranslation } from "react-i18next";
import { changeShowLoading } from "../../components/Spinner/Spinner";
import { Line } from "react-chartjs-2";
import "chartjs-adapter-date-fns";

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
} from "chart.js";

// Registra i componenti necessari
ChartJS.register(
  TimeScale,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

function Readings() {
  const location = useLocation();
  // checkpoints for modal
  const [checkpoints, setCheckpoints] = useState([] as ReadingsCheckpoint[]);
  // all table columns
  const [allColumns, setAllColumns] = useState([] as AllColumnsType[]);
  // all table data
  const [fixedData, setFixedData] = useState([] as FixedDataType[]);
  // modal handler
  const [isReadingsModalOpen, setIsReadingsModalOpen] = useState(false);
  // column clicked to open the modal
  const [clickedColumn, setClickedColumn] = useState({} as CustomColumnClicked);
  // eslint-disable-next-line
  const [sharedInfo, _] = useRecoilState(sharedInternalInfoState);
  const [userInfo, setUserInfo] = useRecoilState(userInfoState);
  const [readingsForChart, setReadingsForChart] = useState([] as any[]);
  const chartRef: any = useRef();
  const [isEditingTime, setIsEditingTime] = useState<boolean>(false);
  const [timeAfterEdit, setTimeAfterEdit] = useState<string>("");
  const [timeBeforeEdit, setTimeBeforeEdit] = useState<string | string[]>("");

  const [currentStatus, setCurrentStatus] = useState("");
  const isDisabled = currentStatus === "validated";

  const [form] = Form.useForm();
  const { t, i18n } = useTranslation();

  const labelColSpan = 14;
  const wrapperColSpan = 10;

  function loadReadings(onlyChart?: boolean) {
    changeShowLoading(
      ReadingsService.load({ uuid: location.state.uuid }).then((resp) => {
        let firstColumns: AllColumnsType[] = [
          {
            title: "Timer",
            dataIndex: "timer",
            fixed: true,
            width: 100,
          },
        ];

        let fixedData: FixedDataType[] = [];
        const checkpointsIndex: Record<string, string> = {};
        const allQuestions = resp?.readings?.map((point: any, idx: string) => {
          fixedData.push({
            key: `timer-${idx}`,
            timer: point?.checkpoint_uuid?.description || "",
            code: point?.checkpoint_uuid?.code,
          });

          checkpointsIndex[point?.checkpoint_uuid?.code] =
            point?.checkpoint_uuid?.description;

          return point?.checkpoint_uuid;
        });

        // check if there are answers on DB
        setCheckpoints(allQuestions);
        // Chart data
        setCurrentStatus(resp?.currentStatus || "");
        drawChart(resp, checkpointsIndex);

        if (resp.anesthesis.length > 0) {
          // per ogni data
          resp.anesthesis.forEach(
            (ane: { date: string; readings: { [key: string]: string } }) => {
              // creo una colonna vuota
              const now = DateTime.fromISO(ane.date, {
                zone: "Europe/Rome",
              }).toFormat("HH:mm:ss");
              const newCol: AllColumnsType = {
                title: now,
                dataIndex: `custom-column-${now}`,
                className: `custom-column-${now}`,
              };
              // per ogni rilevazione
              Object.keys(ane.readings).forEach((key: string) => {
                // se è vuota skippo
                const element = ane.readings[key];
                if (["", undefined].includes(element)) {
                  return;
                }
                // cerco la riga nei dati attuali e aggiungo il valore
                const oldDataIndex = fixedData.findIndex(
                  (el) => el.code === key
                );
                fixedData[oldDataIndex][`custom-column-${now}`] = element;
              });
              firstColumns.push(newCol);
            }
          );
        }

        setAllColumns(sortByTime(firstColumns));

        setFixedData(fixedData);
      })
    );
  }

  useEffect(() => {
    loadReadings();
    // eslint-disable-next-line
  }, []);


  useEffect(() => {
    if (chartRef.current) {
      chartRef.current.update();
    }
  }, [readingsForChart]);

  function drawChart(resp: any, checkpointsIndex: Record<string, string>) {
    const objectData: any = {};

    for (const obj of resp.anesthesis) {
      for (const key of Object.keys(obj.readings)) {
        const value = obj.readings[key];
        if (!objectData[key]) {
          const colors = [
            "#FF6633",
            "#E6B333",
            "#FF33FF",
            "#00B3E6",
            "#FFFF99",
            "#FFB399",
            "#3366E6",
            "#999966",
            "#99FF99",
            "#B34D4D",
          ];
          const colorIndex =
            Object.keys(checkpointsIndex).indexOf(key) % colors.length;
          objectData[key] = {
            label: checkpointsIndex[key],
            data: [],
            fill: true,
            borderColor: colors[colorIndex],
          };
        }
        objectData[key].data.push({ x: obj.date, y: value });
      }
    }

    setReadingsForChart(Object.values(objectData));

    if (chartRef.current) {
      console.log("qua");
      chartRef.current.update();
    }
  }

  function sortByTime(arr: AllColumnsType[]): AllColumnsType[] {
    return arr.sort((a, b) => {
      const timeA: number[] = a.title.split(":").map(Number); // [HH, MM, SS]
      const timeB: number[] = b.title.split(":").map(Number);

      // Confronta le ore
      if (timeA[0] !== timeB[0]) return timeA[0] - timeB[0];
      // Confronta i minuti
      if (timeA[1] !== timeB[1]) return timeA[1] - timeB[1];
      // Confronta i secondi
      return timeA[2] - timeB[2];
    });
  }

  const columnsWithCopyButton = allColumns.map((col) => ({
    ...col,
    title: (
      <div className="flex items-center">
        {col.title}
        {col.title !== "Timer" && (
          <CopyOutlined
            style={{ marginLeft: 10, cursor: "pointer" }}
            onClick={() => duplicateColumn(col)}
          />
        )}
      </div>
    ),
  }));

  async function addGridColumn(): Promise<void> {
    if (
      userInfo.capabilities["CAN_EDIT_ALL_TAB"] ||
      userInfo.capabilities["CAN_EDIT_ANESTHESIS_TAB"]
    ) {
      const now =
        `${DateTime.local().setZone("Europe/Rome").toFormat("HH:mm:ss")}` || "";
      const allTitles = allColumns.map((el) => el.title);
      if (allTitles.includes(now)) {
        return;
      }
      const newCol: AllColumnsType = {
        title: now,
        dataIndex: `custom-column-${now}`,
        className: `custom-column-${now}`,
      };
      await _handleUpdate(newCol);
      setAllColumns((current) => sortByTime([...current, ...[newCol]]));

      // setFixedData((current) => [...current, ...[]]);
    } else {
      <Alert
        message={t("PERMISSION_NEGATE")}
        type="warning"
        showIcon
        closable
      />;
    }
  }

  function removeGridColumn(): void {
    if (
      userInfo.capabilities["CAN_EDIT_ALL_TAB"] ||
      userInfo.capabilities["CAN_EDIT_ANESTHESIS_TAB"]
    ) {
      const columnIndex = clickedColumn.index;
      // -1 not found, 0 is header column
      if (columnIndex > 0) {
        const removedElement = allColumns.splice(columnIndex, 1);
        // riassegno altrimenti non triggera il cambiamento per lo splice
        setAllColumns(() => sortByTime([...allColumns]));
        fixedData.forEach((data) => {
          delete data[removedElement[0].className || ""];
        });
      }
      setIsReadingsModalOpen(false);

      _handleUpdate();
    } else {
      <Alert
        message={t("PERMISSION_NEGATE")}
        type="warning"
        showIcon
        closable
      />;
    }
  }

  // type EventTarget
  const showModal = (event: any) => {
    const customColumnRegex = event.className.match(/custom-column-.*\s/);
    if (!customColumnRegex?.length) {
      // click on first column
      return;
    }
    setIsReadingsModalOpen(true);
    /** start clicked column */

    const customColumnRegexMatch = customColumnRegex[0];
    const customColumn = customColumnRegexMatch.replace(/\s+\S*$/, "");
    const currentColumnIndex = allColumns.findIndex(
      (x) => x.className === customColumn
    );
    const currentColumn: AllColumnsType = allColumns[currentColumnIndex];
    /** end clicked column */
    /** start restore form */
    const formToRestore: { [key: string]: string } = {};
    fixedData.forEach((data) => {
      formToRestore[data.code] = data[customColumn];
    });
    /** end restore form */

    setClickedColumn({
      title: currentColumn.title,
      dataIndex: currentColumn.dataIndex,
      className: currentColumn.className,
      index: currentColumnIndex,
    });
    setTimeAfterEdit(currentColumn.title);
    form.setFieldsValue({ ...formToRestore }); // restore form data
  };

  const handleOk = async () => {
    const formItems = form.getFieldsValue();
    const allTitles = allColumns.map((el) => el.title);

    if ((clickedColumn.title !== timeAfterEdit) && allTitles.includes(timeAfterEdit)) {
      Modal.warning({
        title: t("DUPLICATE_TIME"),
        content: t("CHANGE_TIME"),
      });
      return;
    }
    // console.log("ANSWERS: ", formItems);
    // console.log("QUESTIONS: ", checkpoints);
    // console.log("ALL COLUMNS: ", allColumns);
    // console.log("COLUMN: ", clickedColumn);
    // console.log("DATA: ", fixedData);
    const dataIndex = clickedColumn.dataIndex;
    // fixedData sono le row
    fixedData.forEach((cp) => {
      // per ogni row, in timer c'è la desc del checkpoint
      // cerco il codice del checkpoint
      const foundCheckpointCode = cp?.code || "";
      const answer = formItems[foundCheckpointCode] || "";
      cp[dataIndex] = answer;
      cp["title"] = clickedColumn.title;
    });
    setFixedData([...fixedData]);
    form.resetFields();
    setIsReadingsModalOpen(false);
    await _handleUpdate();
  };

  const handleCancel = () => {
    setIsEditingTime(false);
    setTimeBeforeEdit("");
    setIsReadingsModalOpen(false);
  };

  const handleDelete = () => {
    removeGridColumn();
  };

  async function duplicateColumn(clickedColumn: AllColumnsType) {
    if (
      userInfo.capabilities["CAN_EDIT_ALL_TAB"] ||
      userInfo.capabilities["CAN_EDIT_ANESTHESIS_TAB"]
    ) {

      const now = DateTime.local().setZone("Europe/Rome").toFormat("HH:mm:ss");
      const allTitles = allColumns.map((el) => el.title);
      if (allTitles.includes(now)) {
        return;
      }
      const newDataIndex = `custom-column-${now}`;

      const newCol: AllColumnsType = {
        title: now,
        dataIndex: newDataIndex,
        className: newDataIndex,
      };

      const updatedFixedData = fixedData.map((row) => {
        const copiedValue = row[clickedColumn.dataIndex] ?? "";
        return {
          ...row,
          [newDataIndex]: copiedValue,
        };
      });
      setFixedData(updatedFixedData);

      const valuesToCopy = fixedData.reduce((acc: any, row) => {
        const code = row.code;
        const value = row[clickedColumn.dataIndex] ?? "";
        acc[code] = value;
        return acc;
      }, {});


      const newAllColumns = sortByTime([...allColumns, newCol]);
      setAllColumns(newAllColumns);

      await _handleUpdate(newCol, valuesToCopy);

    } else {
      <Alert
        message={t("PERMISSION_NEGATE")}
        type="warning"
        showIcon
        closable
      />;
    }
  }

  async function _handleUpdate(newCol?: AllColumnsType, valuesToCopy?: Record<string, string>): Promise<void> {
    let currentAllColumns = allColumns;
    /**
     * quando si aggiunge una nuova colonna si arriva qui e
     * la colonna non è stata ancora aggiunta
     */
    if (newCol) {
      const allTitles = currentAllColumns.map((el) => el.title);
      if (!allTitles.includes(newCol.title)) {
        currentAllColumns = [...currentAllColumns, newCol];
      }
    }
    // console.log("QUESTIONS: ", checkpoints);
    // console.log("CURRENT ALL COLUMNS: ", currentAllColumns);
    // console.log("COLUMN: ", clickedColumn);
    // console.log("DATA: ", fixedData);
    const dataColumnsTitle = currentAllColumns.filter(
      (el) => el.title !== "Timer"
    );
    const payload: SavePayload[] = [];
    dataColumnsTitle.forEach((el) => {
      const objToPush: SavePayload = {
        date: DateTime.fromFormat(el.title, "HH:mm:ss", {
          zone: "Europe/Rome",
        }).toISO(),
        readings: {},
      };

      const objTime = DateTime.fromISO(objToPush.date ?? "").toFormat("HH:mm:ss")
      if (objTime === timeBeforeEdit) {
        objToPush.date = DateTime.fromFormat(timeAfterEdit, "HH:mm:ss", {
          zone: "Europe/Rome",
        }).toISO();
      }
      fixedData.forEach((row) => {
        const code = row.code;
        // If this is the duplicated column, use the copied values
        if (newCol && valuesToCopy && el.title === newCol.title) {
          objToPush.readings[code] = valuesToCopy[code] || "";
        } else {
          objToPush.readings[code] = row[`custom-column-${el.title}`] ?? "";
        }
      });
      payload.push(objToPush);
      setIsEditingTime(false);
      setTimeBeforeEdit("");
    });

    // ;

    await changeShowLoading(
      ReadingsService.update(sharedInfo.surgery, {
        tabName: "anesthesis-tab",
        content: payload,
      })
    );

    loadReadings(true);
  }

  return (
    <>
      <Modal
        title={
          <>
            {
              isEditingTime ?
                <TimePicker
                  disabled={!isEditingTime}
                  value={
                    dayjs(timeAfterEdit, "HH:mm:ss", true).isValid()
                      ? dayjs(timeAfterEdit, "HH:mm:ss")
                      : dayjs("12:00:00", "HH:mm:ss")
                  }
                  format="HH:mm:ss"
                  onFocus={() => {
                    setTimeBeforeEdit(clickedColumn.title);
                  }}
                  onChange={(time, timeString) => {
                    setTimeAfterEdit(typeof timeString === "string" ? timeString : "");
                  }}
                /> :
                <>
                  {clickedColumn.title}
                </>
            }

            <Button
              icon={<EditOutlined />}
              disabled={isDisabled}
              onClick={() => {
                setIsEditingTime(!isEditingTime);
              }}
              style={{ marginLeft: 10 }}
            >

            </Button>
          </>
        }
        centered
        open={isReadingsModalOpen}
        width={800}
        closable={false}
        maskClosable={false}
        destroyOnClose
        footer={[
          <Button key="back" onClick={handleCancel} className="float-left">
            {t("CANCEL")}
          </Button>,
          <Button
            type="primary"
            className="mr-10"
            danger
            onClick={handleDelete}
            disabled={isDisabled}
          >
            {t("DELETE")}
          </Button>,

          <Button key="submit" type="primary" onClick={handleOk} disabled={isDisabled}>
            {t("OK")}
          </Button>,
        ]}
      >
        <div className="form-container">
          <Form name="normal_login" className="login-form" form={form} disabled={isDisabled}>
            {checkpoints?.length
              ? checkpoints.map((cp: any, index: number) => {
                return (
                  <>
                    {cp.type === "text" ? (
                      <Form.Item
                        name={cp.code}
                        label={cp.question}
                        labelCol={{
                          span: labelColSpan,
                        }}
                        wrapperCol={{
                          span: wrapperColSpan,
                        }}
                      >
                        <Input.TextArea />
                      </Form.Item>
                    ) : null}
                    {cp.type === "dropdown" ? (
                      <Form.Item
                        name={cp.code}
                        label={cp.question}
                        labelCol={{
                          span: labelColSpan,
                        }}
                        wrapperCol={{
                          span: wrapperColSpan,
                        }}
                      >
                        <Select
                          className="w-full"
                          optionFilterProp="children"
                          fieldNames={{
                            label: "description",
                            value: "key",
                          }}
                          filterOption={(input, option: any) =>
                            option.description
                              .toLowerCase()
                              .indexOf(input.toLowerCase()) >= 0
                          }
                          options={cp.answers}
                        />
                      </Form.Item>
                    ) : null}
                    {cp.type === "boolean" ? (
                      <Form.Item
                        name={cp.code}
                        label={cp.question}
                        labelCol={{
                          span: labelColSpan,
                        }}
                        wrapperCol={{
                          span: wrapperColSpan,
                        }}
                      >
                        <Checkbox></Checkbox>
                      </Form.Item>
                    ) : null}
                  </>
                );
              })
              : null}
          </Form>
        </div>
      </Modal>

      <div className="page-container-margin">
        <div
          style={{ borderBottom: "1px solid #EEE" }}
          className="flex w-full justify-end items-center border-opacity-30 pb-4 text-right"
        >
          <Button
            type="primary"
            icon={<PlusCircleFilled />}
            style={{
              backgroundColor: styles.buttonTeal,
              borderColor: styles.buttonTealHover,
            }}
            onClick={() => addGridColumn()}
            disabled={isDisabled}
          >
            {t("ADD_READING")}
          </Button>
        </div>
        <div
          className="sisos-table-container"
          style={{
            overflowX: "scroll",
            maxWidth: `${window.innerWidth - 350}px`,
          }}
        >
          <Table
            style={{ whiteSpace: "nowrap" }}
            columns={columnsWithCopyButton}
            dataSource={fixedData}
            pagination={false}
            bordered
            size="small"
            className="sisos-table"
            onRow={(record, rowIndex) => {
              return {
                onClick: (event) => {
                  showModal(event.target);
                  //   console.log("click ", typeof event, event);
                }, // click row
                // onDoubleClick: (event) => {}, // double click row
                // onContextMenu: (event) => {}, // right button click row
                // onMouseEnter: (event) => {}, // mouse enter row
                // onMouseLeave: (event) => {}, // mouse leave row
              };
            }}
            onHeaderRow={(columns, index) => {
              return {
                onClick: () => {
                  console.log("header ", columns, index);
                }, // click header row
              };
            }}
          />
        </div>

        {readingsForChart.length > 0 && (
          <div
            className="w-full h-80 bg-gray-50 p-4 mt-4 rounded-md"
            style={{
              border: "1px solid #E5E5E5",
            }}
          >
            <Line
              ref={chartRef}
              data={{
                datasets: readingsForChart,
              }}
              options={{
                responsive: true,
                plugins: {
                  legend: {
                    position: "top",
                  },
                  title: {
                    display: true,
                    // text: "Monthly Average Temperature with Time Series",
                  },
                },
                scales: {
                  x: {
                    position: "right",
                    type: "time", // <--- Tipo di scala "time"
                    time: {
                      unit: "minute", // <--- Configuro l'unità temporale (può essere anche 'day', 'year', ecc.)
                      tooltipFormat: "dd-MM HH:mm:ss", // <--- Formato per il tooltip
                      displayFormats: {
                        minute: "HH:mm:ss", // Visualizzazione sull'asse X con ore, minuti e secondi
                        hour: "HH:mm",
                      },
                    },
                    title: {
                      display: true,
                      text: t("READINGS"),
                    },
                  },
                  y: {
                    position: "right",
                    beginAtZero: true,
                    title: {
                      display: true,
                      // text: "Temperature (°C)",
                    },
                  },
                },
                maintainAspectRatio: false,
              }}
            ></Line>
          </div>
        )}
      </div>
    </>
  );
}

export default Readings;
